import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  OnChanges,
  SimpleChanges,
  input,
  model,
  signal,
} from '@angular/core';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';

@Directive({
  selector: '[appScrollSpy]',
  exportAs: 'appScrollSpy',
})
export class ScrollSpyDirective implements OnChanges {
  readonly ids = input<string[]>([]);
  readonly offset = input<number>();
  readonly activeSection = model<string | undefined>();

  private readonly disableScrollListener = signal<
    | {
        id: string;
        disabled: boolean;
      }
    | undefined
  >(undefined);

  public async scrollToSectionId(id: string | undefined) {
    if (!id) {
      return;
    }
    this.disableScrollListener.set({
      id,
      disabled: true,
    });

    if (this.elementRef && this.elementRef.nativeElement) {
      const topOffset = this.elementRef.nativeElement.offsetHeight || 30;
      this.viewportScroller.setOffset([0, topOffset]);
    }
    this.viewportScroller.scrollToAnchor(id);
    // there is no way to detect when the scroll is done,
    // so we disable the scroll listener for a short time
    setTimeout(() => {
      if (this.disableScrollListener()?.id === id) {
        this.disableScrollListener.set(undefined);
      }
    }, 1500);
  }

  private currentSection!: string;

  constructor(
    @Inject(DOCUMENT)
    private document: Document,
    private settingsSelector: SettingsStoreSelector,
    private readonly viewportScroller: ViewportScroller,
    private readonly elementRef: ElementRef,
  ) {}

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.disableScrollListener()) {
      // If the scroll listener is disabled, do not check the position
      // used when the user clicks on a section link to scroll to it
      return;
    }
    this.checkPosition();
  }

  checkPosition(): void {
    const ids = this.ids();
    if (!ids?.length) {
      return;
    }
    let currentSection: string | undefined;
    const pageYOffset = window.scrollY;
    const isDesktop = this.settingsSelector.isDesktopScreen;
    const offsetValue = this.offset() ?? (isDesktop ? 70 : 55);
    // Sort elements by offsetTop ascending
    const elements = ids
      .map(tag => this.document.getElementById(tag) as HTMLElement)
      .filter(el => !!el)
      .sort((a, b) => a.offsetTop - b.offsetTop);

    // Iterate in reverse to find the last section above the scroll position
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      if (pageYOffset + offsetValue >= el.offsetTop) {
        currentSection = el.id;
        break;
      }
    }

    if (currentSection && currentSection !== this.currentSection) {
      this.currentSection = currentSection;
      this.activeSection.set(this.currentSection);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { ids } = changes;
    if (ids && ids.currentValue) {
      setTimeout(() => {
        this.checkPosition();
      });
    }
  }
}
